// posterService.js — Poster upload, rate limiting, and fetch logic

import { supabase } from './supabase/client';

const BUCKET = 'posters';
const DAILY_LIMIT = 10;
const EXPIRE_DAYS = 21;
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

// ─────────────────────────────────────────────
// Rate limiting: count today's uploads by IP
// ─────────────────────────────────────────────

/**
 * Get caller's approximate IP via a free public API.
 * Falls back to null if unavailable.
 */
export const getClientIp = async () => {
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const json = await res.json();
    return json.ip || null;
  } catch {
    return null;
  }
};

/**
 * Check how many posters this IP has uploaded today.
 * Returns { count, limitReached }
 */
export const checkDailyLimit = async ip => {
  if (!ip) return { count: 0, limitReached: false };

  try {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);

    const { count, error } = await supabase
      .from('posters')
      .select('id', { count: 'exact', head: true })
      .eq('uploader_ip', ip)
      .gte('created_at', startOfDay.toISOString());

    if (error) {
      console.error('[posterService] checkDailyLimit error:', error);
      return { count: 0, limitReached: false };
    }

    return {
      count: count || 0,
      limitReached: (count || 0) >= DAILY_LIMIT,
    };
  } catch (err) {
    console.error('[posterService] checkDailyLimit exception:', err);
    return { count: 0, limitReached: false };
  }
};

// ─────────────────────────────────────────────
// Upload poster image
// ─────────────────────────────────────────────

/**
 * Validate file before upload.
 * Returns { valid, error }
 */
export const validateFile = file => {
  if (!file) return { valid: false, error: 'No file selected.' };

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File is too large. Maximum size is 2MB (your file: ${(file.size / 1024 / 1024).toFixed(1)}MB).`,
    };
  }

  const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  if (!allowed.includes(file.type)) {
    return {
      valid: false,
      error: 'Only JPEG, PNG, WebP, and GIF images are allowed.',
    };
  }

  return { valid: true, error: null };
};

/**
 * Upload a poster image and create a database record.
 * @param {File} file - image file
 * @param {string|null} ip - uploader IP for rate limiting
 * @returns {Promise<{ success, data, error }>}
 */
export const uploadPoster = async (file, ip = null) => {
  try {
    // 1. Validate file
    const { valid, error: validationError } = validateFile(file);
    if (!valid) return { success: false, error: validationError };

    // 2. Check daily limit
    const { limitReached } = await checkDailyLimit(ip);
    if (limitReached) {
      return { success: false, error: 'LIMIT_REACHED' };
    }

    // 3. Detect whether the uploader is a registered (logged-in) user
    const { data: { user } } = await supabase.auth.getUser();
    const isLoggedIn = !!user;

    // 4. Generate unique storage path
    const ext = file.name.split('.').pop() || 'jpg';
    const timestamp = Date.now();
    const random = Math.random().toString(36).slice(2, 8);
    const folder = isLoggedIn ? `users/${user.id}` : 'anonymous';
    const storagePath = `${folder}/${timestamp}-${random}.${ext}`;

    // 5. Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type,
      });

    if (uploadError) {
      console.error('[posterService] Storage upload error:', uploadError);
      return { success: false, error: 'Upload failed. Please try again.' };
    }

    // 6. Get public URL
    const { data: urlData } = supabase.storage
      .from(BUCKET)
      .getPublicUrl(storagePath);

    const imageUrl = urlData?.publicUrl;
    if (!imageUrl) {
      return { success: false, error: 'Could not get image URL after upload.' };
    }

    // 7. Build record — registered users get permanent storage, anonymous get 21 days
    const expiresAt = isLoggedIn
      ? null
      : (() => {
          const d = new Date();
          d.setDate(d.getDate() + EXPIRE_DAYS);
          return d.toISOString();
        })();

    const { data: poster, error: insertError } = await supabase
      .from('posters')
      .insert({
        image_url: imageUrl,
        storage_path: storagePath,
        uploader_ip: ip,
        user_id: isLoggedIn ? user.id : null,
        is_permanent: isLoggedIn,
        expires_at: expiresAt,
      })
      .select()
      .single();

    if (insertError) {
      console.error('[posterService] DB insert error:', insertError);
      await supabase.storage.from(BUCKET).remove([storagePath]);
      return { success: false, error: 'Failed to save poster. Please try again.' };
    }

    console.log(
      '[posterService] Poster uploaded successfully:',
      poster.id,
      isLoggedIn ? '(permanent)' : '(expires in 21 days)',
    );
    return { success: true, data: poster, isPermanent: isLoggedIn };
  } catch (err) {
    console.error('[posterService] uploadPoster exception:', err);
    return { success: false, error: 'Something went wrong. Please try again.' };
  }
};

// ─────────────────────────────────────────────
// Fetch active posters for CollaborationGrid
// ─────────────────────────────────────────────

/**
 * Fetch all active (non-expired) posters, newest first.
 * @returns {Promise<{ success, data, error }>}
 */
export const getActivePosters = async () => {
  try {
    const { data, error } = await supabase
      .from('posters')
      .select('id, image_url, storage_path, user_id, created_at, expires_at, is_permanent')
      .or(`is_permanent.eq.true,expires_at.gt.${new Date().toISOString()}`)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[posterService] getActivePosters error:', error);
      return { success: false, error: error.message, data: [] };
    }

    return { success: true, data: data || [] };
  } catch (err) {
    console.error('[posterService] getActivePosters exception:', err);
    return { success: false, error: err.message, data: [] };
  }
};

// ─────────────────────────────────────────────
// Delete a poster (Storage file + DB row)
// ─────────────────────────────────────────────

/**
 * Permanently delete a poster: removes the file from Storage and the DB row.
 * Only the poster owner should be able to call this (enforced by RLS on DELETE).
 * @param {string} id - poster UUID
 * @param {string} storagePath - e.g. "users/uid/timestamp-abc.jpg"
 */
export const deletePoster = async (id, storagePath) => {
  try {
    // 1. Remove file from Supabase Storage
    const { error: storageError } = await supabase.storage
      .from(BUCKET)
      .remove([storagePath]);

    if (storageError) {
      // Log but continue — DB row must still be cleaned up
      console.error('[posterService] Storage delete error:', storageError);
    }

    // 2. Delete DB record
    const { error: dbError } = await supabase
      .from('posters')
      .delete()
      .eq('id', id);

    if (dbError) {
      console.error('[posterService] DB delete error:', dbError);
      return { success: false, error: dbError.message };
    }

    console.log('[posterService] Poster deleted:', id);
    return { success: true };
  } catch (err) {
    console.error('[posterService] deletePoster exception:', err);
    return { success: false, error: err.message };
  }
};
